import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { formatXof, PAYMENT_METHODS, INVOICE_STATUS } from '../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'OUVERTE', label: 'Ouverte' },
  { value: 'PARTIELLE', label: 'Partielle' },
  { value: 'PAYEE', label: 'Payée' },
  { value: 'ANNULEE', label: 'Annulée' },
];

function isScholarshipFee(label) {
  const l = (label || '').toLowerCase();
  return l.includes('scolarit') || l.includes('mensualit');
}

function applyScholarship(amount, sch) {
  if (!sch?.active || !sch.value) return amount;
  if (sch.type === 'PERCENTAGE') return Math.round(amount * (1 - sch.value / 100));
  return Math.max(0, amount - sch.value);
}

function groupInvoicesByStudent(invoices) {
  const map = new Map();
  for (const inv of invoices) {
    const invNum = inv.invoiceNumber || inv.number || inv.invoiceNo || null;
    // fallback: utiliser le matricule comme clé de regroupement si pas de numéro
    const key = invNum || inv.studentId;
    if (!map.has(key)) {
      map.set(key, {
        key,
        invoiceNumber: invNum || null,
        student: inv.student,
        studentId: inv.studentId,
        lines: [],
      });
    }
    map.get(key).lines.push(inv);
  }
  return Array.from(map.values()).map((g) => {
    const totalAmount = g.lines.reduce((s, i) => s + i.amountXof, 0);
    const totalPaid   = g.lines.reduce((s, i) => s + (i.paid ?? 0), 0);
    const totalBalance = totalAmount - totalPaid;
    const statuses = new Set(g.lines.map((i) => i.status));
    let status = 'OUVERTE';
    if (statuses.size === 1 && statuses.has('PAYEE')) status = 'PAYEE';
    else if (statuses.has('PAYEE') || statuses.has('PARTIELLE')) status = 'PARTIELLE';
    else if (statuses.size === 1 && statuses.has('ANNULEE')) status = 'ANNULEE';
    return { ...g, totalAmount, totalPaid, totalBalance, status };
  });
}

export default function FacturesInscription() {
  const [invoices, setInvoices]   = useState([]);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [expanded, setExpanded]   = useState(new Set());
  const [payModal, setPayModal]   = useState(null);
  const [payForm, setPayForm]     = useState({ amountXof: '', method: 'ESPECES', reference: '' });
  const [saving, setSaving]       = useState(false);
  const [receipt, setReceipt]     = useState(null);
  const [groupModal, setGroupModal] = useState(null); // { group, method, reference, scholarship }
  const [groupReceipts, setGroupReceipts] = useState([]);
  const [scholarshipInfo, setScholarshipInfo] = useState(null); // pour le modal paiement individuel

  function load() {
    setLoading(true);
    api.get('/finance/invoices' + (statusFilter ? `?status=${statusFilter}` : ''))
      .then(setInvoices)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [statusFilter]);

  const groups = useMemo(() => {
    const all = groupInvoicesByStudent(invoices);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((g) => {
      const name = `${g.student?.lastName ?? ''} ${g.student?.firstName ?? ''} ${g.student?.matricule ?? ''} ${g.invoiceNumber}`.toLowerCase();
      return name.includes(q);
    });
  }, [invoices, search]);

  function toggleExpand(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function openPay(inv) {
    setReceipt(null);
    setScholarshipInfo(null);
    const baseAmount = inv.balance > 0 ? inv.balance : inv.amountXof;
    let sch = null;
    let finalAmount = baseAmount;
    try {
      const student = await api.get(`/students/${inv.studentId}`);
      const s = student?.extra?.scholarship;
      if (s?.active && s.value > 0 && isScholarshipFee(inv.label)) {
        sch = s;
        finalAmount = applyScholarship(baseAmount, s);
      }
    } catch (_) {}
    setScholarshipInfo(sch);
    setPayForm({ amountXof: String(finalAmount), method: 'ESPECES', reference: '' });
    setPayModal(inv);
  }

  async function openGroupPay(group) {
    let sch = null;
    try {
      const student = await api.get(`/students/${group.studentId}`);
      const s = student?.extra?.scholarship;
      if (s?.active && s.value > 0) sch = s;
    } catch (_) {}
    setGroupModal({ group, method: 'ESPECES', reference: '', scholarship: sch });
    setGroupReceipts([]);
  }

  async function submitGroupPayment() {
    if (!groupModal) return;
    setSaving(true); setError('');
    const unpaid = groupModal.group.lines.filter((l) => l.status !== 'PAYEE' && l.status !== 'ANNULEE');
    const results = [];
    try {
      for (const inv of unpaid) {
        const baseAmount = inv.balance > 0 ? inv.balance : inv.amountXof;
        let amount = baseAmount;
        if (groupModal.scholarship && isScholarshipFee(inv.label)) {
          amount = applyScholarship(baseAmount, groupModal.scholarship);
        }
        const r = await api.post('/finance/payments', {
          studentId: inv.studentId,
          invoiceId: inv.id,
          amountXof: amount,
          method: groupModal.method,
          reference: groupModal.reference || undefined,
        });
        results.push(r);
      }
      setGroupReceipts(results);
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function submitPayment() {
    if (!payModal) return;
    setSaving(true); setError('');
    try {
      const result = await api.post('/finance/payments', {
        studentId: payModal.studentId,
        invoiceId: payModal.id,
        amountXof: parseInt(payForm.amountXof, 10),
        method: payForm.method,
        reference: payForm.reference || undefined,
      });
      setReceipt(result);
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function statusBadge(status) {
    const [color, label] = INVOICE_STATUS[status] ?? ['gray', status];
    const bg  = color === 'green' ? '#d1fae5' : color === 'gold' ? '#fef9c3' : color === 'red' ? '#fee2e2' : '#f3f4f6';
    const txt = color === 'green' ? '#065f46' : color === 'gold' ? '#92400e' : color === 'red' ? '#991b1b' : '#374151';
    return <span style={{ background: bg, color: txt, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>{label}</span>;
  }


  async function printReceipt() {
    let school = {};
    try { school = await api.get('/establishment'); } catch (_) {}

    const student = payModal?.student ?? {};
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR');
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const methodLabel = PAYMENT_METHODS[payForm.method] ?? payForm.method;
    const schoolName = school.name ?? 'Établissement';
    const schoolLevel = school.level ?? school.type ?? '';

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Reçu ${receipt?.receiptNumber ?? ''}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; width: 72mm; margin: 0 auto; padding: 8px; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  th, td { font-size: 11px; padding: 2px 4px; }
  th { text-align: left; }
  td:last-child { text-align: right; }
  .section-title { text-align: center; font-weight: bold; font-size: 11px; margin: 6px 0 4px; }
  .row2 { display: flex; justify-content: space-between; }
  @media print { body { width: 100%; } }
</style>
</head><body>
<div class="center">
  <div class="bold" style="font-size:14px">${schoolName.toUpperCase()}</div>
  ${schoolLevel ? `<div>${schoolLevel.toUpperCase()}</div>` : ''}
</div>
<div class="line"></div>
<div class="row2"><span>Imprimé le :</span><span>${dateStr} ${timeStr}</span></div>
<div class="row2"><span>Emis le :</span><span>${dateStr}</span></div>
<div class="line"></div>
<div class="center bold" style="font-size:13px">RECU N° ${receipt?.receiptNumber ?? ''}</div>
<div class="center">Élève : ${student.matricule ?? ''}</div>
<div class="center bold" style="font-size:14px; margin:4px 0">${(student.lastName ?? '') + ' ' + (student.firstName ?? '')}</div>
<div class="line"></div>
<div class="row2"><span><b>Niveau</b></span><span><b>Classe</b></span></div>
<div class="row2"><span>${student.level ?? '—'}</span><span>${student.className ?? payModal?.className ?? '—'}</span></div>
<div class="line"></div>
<div class="row2"><span>Quittance n°</span><span>Référence</span></div>
<div class="row2"><span>${receipt?.receiptNumber ?? ''}</span><span>${payForm.reference || '—'}</span></div>
<div class="line"></div>
<div class="row2"><span><b>Mode règlement</b></span><span><b>Montant réglé</b></span></div>
<div class="row2"><span>${methodLabel}</span><span><b>${Number(payForm.amountXof).toLocaleString('fr-FR')} F</b></span></div>
<div class="line"></div>
<div class="section-title">——— FRAIS FACTURES ———</div>
<table>
  <tr><th>Intitulé frais</th><th style="text-align:right">Montant</th></tr>
  <tr><td>${payModal?.label ?? ''}</td><td style="text-align:right">${Number(payForm.amountXof).toLocaleString('fr-FR')}</td></tr>
</table>
<div class="line"></div>
<div class="row2"><span>Caisse :</span><span>${school.cashierName ?? school.adminName ?? ''}</span></div>
<div class="line"></div>
<div class="center" style="font-size:10px; margin-top:6px">Schoolizy — www.schoolizy.com</div>
</body></html>`;

    const w = window.open('', '_blank', 'width=420,height=640');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  }

  async function printGroupInvoice(g) {
    let school = {};
    try { school = await api.get('/establishment'); } catch (_) {}
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR');
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const schoolName = school.name || 'Établissement';
    const schoolLevel = school.level ?? school.type ?? '';
    const student = g.student || {};
    const studentName = `${student.lastName ?? ''} ${student.firstName ?? ''}`.trim();
    const matricule = student.matricule || '';

    const statusLabel = (s) => {
      if (s === 'PAYEE') return 'Payée';
      if (s === 'PARTIELLE') return 'Partielle';
      if (s === 'ANNULEE') return 'Annulée';
      return 'Ouverte';
    };

    const rows = g.lines.map((inv) => `
      <tr>
        <td>${inv.label ?? ''}</td>
        <td class="right">${(inv.amountXof ?? 0).toLocaleString('fr-FR')}</td>
        <td class="right">${(inv.paid ?? 0).toLocaleString('fr-FR')}</td>
        <td class="right">${(inv.balance ?? 0).toLocaleString('fr-FR')}</td>
        <td class="center">${statusLabel(inv.status)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Facture ${g.invoiceNumber ?? ''}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; padding: 20px; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 8px 0; }
  .row { display: flex; justify-content: space-between; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { background: #f3f4f6; padding: 7px 10px; text-align: left; font-size: 11px; border: 1px solid #ddd; }
  th.right { text-align: right; }
  th.center { text-align: center; }
  td { padding: 7px 10px; border: 1px solid #e5e7eb; font-size: 12px; }
  tfoot td { font-weight: 700; background: #f9fafb; }
  @media print { body { padding: 8px; } }
</style>
</head><body>
<div class="center bold" style="font-size:16px">${schoolName}</div>
${schoolLevel ? `<div class="center" style="font-size:12px">${schoolLevel}</div>` : ''}
<div class="line"></div>
<div class="center bold" style="font-size:14px; margin:8px 0">FACTURE D'INSCRIPTION</div>
<div class="row"><span>N° Facture :</span><span class="bold">${g.invoiceNumber ?? '—'}</span></div>
<div class="row"><span>Date :</span><span>${dateStr} ${timeStr}</span></div>
<div class="line"></div>
<div class="row"><span>Élève :</span><span class="bold">${studentName}</span></div>
<div class="row"><span>Matricule :</span><span>${matricule}</span></div>
<div class="line"></div>
<table>
  <thead>
    <tr>
      <th>Type de frais</th>
      <th class="right">Montant</th>
      <th class="right">Payé</th>
      <th class="right">Reste</th>
      <th class="center">Statut</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr>
      <td>Total</td>
      <td class="right">${(g.totalAmount ?? 0).toLocaleString('fr-FR')} F</td>
      <td class="right">${(g.totalPaid ?? 0).toLocaleString('fr-FR')} F</td>
      <td class="right">${(g.totalBalance ?? 0).toLocaleString('fr-FR')} F</td>
      <td></td>
    </tr>
  </tfoot>
</table>
<div class="line" style="margin-top:16px"></div>
<div class="center" style="font-size:10px">Schoolizy — www.schoolizy.com</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=700,height=700');
    if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 400); }
  }

  async function printFromInvoice(inv) {
    let school = {};
    try { school = await api.get('/establishment'); } catch (_) {}
    let payment = null;
    try {
      const pmts = await api.get('/finance/payments?invoiceId=' + inv.id);
      payment = Array.isArray(pmts) ? pmts[pmts.length - 1] : pmts;
    } catch (_) {}
    const now = payment?.createdAt ? new Date(payment.createdAt) : new Date();
    const dateStr = now.toLocaleDateString('fr-FR');
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const schoolName = school.name || 'Établissement';
    const studentName = inv.student?.firstName ? (inv.student.firstName + ' ' + (inv.student.lastName || '')) : (inv.studentName || '');
    const matricule = inv.student?.matricule || inv.matricule || '';
    const receiptNo = inv.invoiceNumber || inv.id?.slice(0,8).toUpperCase() || '—';
    const amount = inv.totalAmount || inv.amount || 0;
    const paid = inv.paidAmount || amount;
    const modePay = payment?.method || payment?.paymentMethod || 'Espèces';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Reçu</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; margin: 0; padding: 16px; width: 320px; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 8px 0; }
  .row { display: flex; justify-content: space-between; margin: 4px 0; }
</style>
</head><body>
<div class="center bold" style="font-size:15px">${schoolName}</div>
<div class="center" style="font-size:11px">REÇU DE PAIEMENT</div>
<div class="line"></div>
<div class="row"><span>N° Reçu :</span><span class="bold">${receiptNo}</span></div>
<div class="row"><span>Date :</span><span>${dateStr} ${timeStr}</span></div>
<div class="line"></div>
<div class="row"><span>Élève :</span><span class="bold">${studentName}</span></div>
<div class="row"><span>Matricule :</span><span>${matricule}</span></div>
<div class="line"></div>
<div class="row"><span>Motif :</span><span>Frais d'inscription</span></div>
<div class="row"><span>Montant :</span><span class="bold">${paid.toLocaleString('fr-FR')} F CFA</span></div>
<div class="row"><span>Mode :</span><span>${modePay}</span></div>
<div class="line"></div>
<div class="center" style="margin-top:12px;font-size:11px">Merci de votre confiance</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=400,height=600');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 500);
    }
  }

  return (
    <div>
      <div className="card-head" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Règlement — Factures d'inscription</h2>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="search-input"
          placeholder="Rechercher élève, matricule, n° facture…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 140 }}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : groups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Aucune facture trouvée.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map((g) => {
            const open = expanded.has(g.key);
            const hasUnpaid = g.lines.some((l) => l.status !== 'PAYEE' && l.status !== 'ANNULEE');
            return (
              <div key={g.key} style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface)' }}>
                {/* En-tête facture */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', background: open ? 'var(--surface-soft)' : undefined }}
                  onClick={() => toggleExpand(g.key)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{g.student?.lastName} {g.student?.firstName}</span>
                      <span className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{g.student?.matricule}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {g.invoiceNumber && (
                        <>
                          <span style={{ marginRight: 4 }}>N° Facture :</span>
                          <span className="mono" style={{ fontWeight: 700, color: 'var(--primary)', marginRight: 8 }}>{g.invoiceNumber}</span>
                        </>
                      )}
                      <span>{g.lines.length} poste{g.lines.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{formatXof(g.totalAmount)}</div>
                    <div style={{ fontSize: 11, color: g.totalBalance > 0 ? 'var(--danger)' : 'var(--success)' }}>
                      Reste : {formatXof(g.totalBalance)}
                    </div>
                  </div>
                  {statusBadge(g.status)}
                  {hasUnpaid && (
                    <button
                      className="btn btn-sm btn-primary"
                      style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                      onClick={(e) => { e.stopPropagation(); openGroupPay(g); }}
                    >Tout régler</button>
                  )}
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Imprimer la facture complète"
                    style={{ marginLeft: 4 }}
                    onClick={(e) => { e.stopPropagation(); printGroupInvoice(g); }}
                  >🖨️</button>
                  <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
                </div>

                {/* Lignes de frais */}
                {open && (
                  <div style={{ borderTop: '1px solid var(--line-soft)' }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--surface-soft)' }}>
                          <th style={{ padding: '7px 16px', textAlign: 'left', fontWeight: 600 }}>Type de frais</th>
                          <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600 }}>Montant</th>
                          <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600 }}>Payé</th>
                          <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600 }}>Reste</th>
                          <th style={{ padding: '7px 12px', textAlign: 'center', fontWeight: 600 }}>Statut</th>
                          <th style={{ padding: '7px 12px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.lines.map((inv) => (
                          <tr key={inv.id} style={{ borderTop: '1px solid var(--line-soft)' }}>
                            <td style={{ padding: '8px 16px' }}>{inv.label}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatXof(inv.amountXof)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--success)' }}>{formatXof(inv.paid ?? 0)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: inv.balance > 0 ? 'var(--danger)' : 'inherit' }}>
                              {formatXof(inv.balance ?? 0)}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>{statusBadge(inv.status)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                {inv.status !== 'PAYEE' && inv.status !== 'ANNULEE' && (
                                  <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); openPay(inv); }}>Régler</button>
                                )}
                                {inv.status === 'PAYEE' && (
                                  <button className="btn btn-sm btn-ghost" title="Imprimer le reçu" onClick={(e) => { e.stopPropagation(); printFromInvoice(inv); }}>🖨️</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid var(--line)', fontWeight: 700, background: 'var(--surface-soft)' }}>
                          <td style={{ padding: '8px 16px' }}>Total</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatXof(g.totalAmount)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--success)' }}>{formatXof(g.totalPaid)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: g.totalBalance > 0 ? 'var(--danger)' : 'inherit' }}>{formatXof(g.totalBalance)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal règlement groupé */}
      {groupModal && (
        <div className="modal-overlay" onClick={() => !saving && setGroupModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Régler toutes les factures</h3>
              <button className="modal-close" onClick={() => setGroupModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {groupReceipts.length > 0 ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{groupReceipts.length} paiement{groupReceipts.length > 1 ? 's' : ''} enregistré{groupReceipts.length > 1 ? 's' : ''}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
                    {groupReceipts.map((r, i) => (
                      <div key={i}>Reçu <strong>{r.receiptNumber}</strong> — {formatXof(r.amountXof)}</div>
                    ))}
                  </div>
                  <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => setGroupModal(null)}>Fermer</button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-alt)', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600 }}>{groupModal.group.student?.lastName} {groupModal.group.student?.firstName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {groupModal.group.lines.filter((l) => l.status !== 'PAYEE' && l.status !== 'ANNULEE').length} facture(s) à régler
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Total restant : <strong style={{ color: 'var(--danger)' }}>{formatXof(groupModal.group.totalBalance)}</strong>
                    </div>
                  </div>
                  {groupModal.scholarship && (
                    <div style={{ marginBottom: 12, padding: '6px 12px', background: '#fef9c3', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                      🎓 Bourse active — {groupModal.scholarship.type === 'PERCENTAGE' ? `${groupModal.scholarship.value}%` : `${Number(groupModal.scholarship.value).toLocaleString('fr-FR')} XOF`} de réduction appliquée sur scolarité / mensualité
                    </div>
                  )}
                  <div style={{ marginBottom: 12, maxHeight: 160, overflowY: 'auto', fontSize: 12 }}>
                    {groupModal.group.lines.filter((l) => l.status !== 'PAYEE' && l.status !== 'ANNULEE').map((l) => {
                      const baseAmount = l.balance > 0 ? l.balance : l.amountXof;
                      const finalAmount = (groupModal.scholarship && isScholarshipFee(l.label))
                        ? applyScholarship(baseAmount, groupModal.scholarship)
                        : baseAmount;
                      return (
                        <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--line-soft)' }}>
                          <span>{l.label}</span>
                          <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                            {formatXof(finalAmount)}
                            {finalAmount !== baseAmount && <span style={{ color: 'var(--muted)', textDecoration: 'line-through', marginLeft: 6 }}>{formatXof(baseAmount)}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mode de paiement</label>
                    <select className="form-select" value={groupModal.method} onChange={(e) => setGroupModal((p) => ({ ...p, method: e.target.value }))}>
                      {Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Référence (optionnel)</label>
                    <input type="text" className="form-control" value={groupModal.reference}
                      onChange={(e) => setGroupModal((p) => ({ ...p, reference: e.target.value }))}
                      placeholder="N° chèque, virement…" />
                  </div>
                  {error && <div className="alert alert-danger">{error}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-primary" onClick={submitGroupPayment} disabled={saving}>
                      {saving ? 'Enregistrement…' : `Confirmer — ${formatXof(groupModal.group.lines.filter((l) => l.status !== 'PAYEE' && l.status !== 'ANNULEE').reduce((sum, l) => { const base = l.balance > 0 ? l.balance : l.amountXof; return sum + ((groupModal.scholarship && isScholarshipFee(l.label)) ? applyScholarship(base, groupModal.scholarship) : base); }, 0))}`}
                    </button>
                    <button className="btn btn-ghost" onClick={() => setGroupModal(null)}>Annuler</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal paiement */}
      {payModal && (
        <div className="modal-overlay" onClick={() => !saving && setPayModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Enregistrer un paiement</h3>
              <button className="modal-close" onClick={() => setPayModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {receipt ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Paiement enregistré</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Reçu : <strong>{receipt.receiptNumber}</strong>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Montant : <strong>{formatXof(receipt.amountXof)}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                    <button className="btn btn-primary" onClick={printReceipt}>🖨️ Imprimer</button>
                    <button className="btn btn-ghost" onClick={() => setPayModal(null)}>Fermer</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-alt)', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600 }}>{payModal.student?.lastName} {payModal.student?.firstName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{payModal.label}</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Reste à payer : <strong style={{ color: 'var(--danger)' }}>{formatXof(payModal.balance > 0 ? payModal.balance : payModal.amountXof)}</strong>
                    </div>
                  </div>
                  {scholarshipInfo && (
                    <div style={{ marginBottom: 12, padding: '6px 12px', background: '#fef9c3', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                      🎓 Bourse appliquée : {scholarshipInfo.type === 'PERCENTAGE' ? `${scholarshipInfo.value}%` : `${Number(scholarshipInfo.value).toLocaleString('fr-FR')} XOF`} de réduction
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Montant (F CFA)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={payForm.amountXof}
                      onChange={(e) => setPayForm((p) => ({ ...p, amountXof: e.target.value }))}
                      min={1}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mode de paiement</label>
                    <select className="form-select" value={payForm.method} onChange={(e) => setPayForm((p) => ({ ...p, method: e.target.value }))}>
                      {Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Référence (optionnel)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={payForm.reference}
                      onChange={(e) => setPayForm((p) => ({ ...p, reference: e.target.value }))}
                      placeholder="N° chèque, virement…"
                    />
                  </div>
                  {error && <div className="alert alert-danger">{error}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-primary" onClick={submitPayment} disabled={saving || !payForm.amountXof}>
                      {saving ? 'Enregistrement…' : 'Confirmer'}
                    </button>
                    <button className="btn btn-ghost" onClick={() => setPayModal(null)}>Annuler</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
